import Phaser from 'phaser';
import { POPUP_OVERLAY_DEPTH } from '@constants/common.const';
import KawaseBlurFilterPipelinePlugin from 'phaser3-rex-plugins/plugins/kawaseblurpipeline-plugin';

/**
 * Captures game + UI scenes into a RenderTexture and applies a kawase blur.
 * Used by ModalSheetScrimManager and Finish scene to create blurred backdrops.
 */
export class BlurBackground {
  private bgTexture?: Phaser.GameObjects.RenderTexture;
  private bgImage?: Phaser.GameObjects.Image;
  private readonly blurName: string;
  private readonly depth: number;
  private _captured = false;

  constructor(
    private scene: Phaser.Scene,
    textureKey: string,
    depth?: number,
  ) {
    this.blurName = `${textureKey}_blur`;
    this.depth = depth ?? POPUP_OVERLAY_DEPTH - 1;
  }

  /** The blurred background image (if created). */
  get image(): Phaser.GameObjects.Image | undefined {
    return this.bgImage;
  }

  /**
   * Capture the game scene (and optionally UI scene) into a blurred image.
   *
   * @param gameScene  The game scene to capture (scroll-compensated).
   * @param options
   * @param options.uiScene   Optional UI scene to draw on top.
   * @param options.exclude   Extra objects to exclude from drawing (e.g. scrim).
   *                          The BlurBackground's own objects are excluded automatically.
   */
  capture(
    gameScene: Phaser.Scene,
    options?: {
      uiScene?: Phaser.Scene;
      exclude?: Phaser.GameObjects.GameObject[];
    },
  ): void {
    // If already captured and the scene hasn't changed, just show the cached result
    if (this._captured && this.bgTexture && this.bgImage) {
      this.bgImage.setVisible(true);
      return;
    }

    const { width, height } = this.scene.scale;

    // Create or reuse RenderTexture (no saveTexture — avoids continuous GPU re-uploads)
    if (!this.bgTexture) {
      this.bgTexture = this.scene.add.renderTexture(0, 0, width, height);
      this.bgTexture.setVisible(false);
    } else {
      this.bgTexture.resize(width, height);
      this.bgTexture.clear();
    }

    // Build exclusion set (always exclude our own objects + caller-provided)
    const excludeSet = new Set<Phaser.GameObjects.GameObject>(
      options?.exclude ?? [],
    );
    if (this.bgTexture) excludeSet.add(this.bgTexture);
    if (this.bgImage) excludeSet.add(this.bgImage);

    // Draw game scene — split by scrollFactor so fixed objects aren't displaced
    const cam = gameScene.cameras.main;
    const gameList = gameScene.children.list.filter((c) => !excludeSet.has(c));
    const scrolling = gameList.filter(
      (c) =>
        !('scrollFactorY' in c) ||
        (c as Phaser.GameObjects.Image).scrollFactorY !== 0,
    );
    const fixed = gameList.filter(
      (c) =>
        'scrollFactorY' in c &&
        (c as Phaser.GameObjects.Image).scrollFactorY === 0,
    );
    this.bgTexture.draw(fixed);
    this.bgTexture.draw(scrolling, -cam.scrollX, -cam.scrollY);

    // Draw UI scene on top (excluding our objects + caller's exclusions)
    if (options?.uiScene) {
      const uiList = options.uiScene.children.list.filter(
        (c) => !excludeSet.has(c),
      );
      this.bgTexture.draw(uiList);
    }

    // Create or update the blurred image — always reference bgTexture.texture directly
    if (!this.bgImage) {
      this.bgImage = this.scene.add.image(0, 0, this.bgTexture.texture);
      this.bgImage.setOrigin(0, 0);
      this.bgImage.setDepth(this.depth);
    } else {
      this.bgImage.setTexture(this.bgTexture.texture.key);
    }
    this.bgImage.setDisplaySize(width, height);
    this.bgImage.setVisible(true);

    // Apply kawase blur
    const plugin = this.scene.plugins.get('rexKawaseBlurPipeline') as
      | KawaseBlurFilterPipelinePlugin
      | undefined;
    if (plugin) {
      plugin.remove(this.bgImage, this.blurName);
      plugin.add(this.bgImage, {
        blur: 10,
        quality: 20,
        pixelWidth: 0.1,
        pixelHeight: 0.1,
        name: this.blurName,
      });
    }

    this._captured = true;
  }

  /** Update sizes after a window resize. */
  updateSize(width: number, height: number): void {
    if (this.bgTexture) {
      this.bgTexture.resize(width, height);
      this.bgTexture.clear();
    }
    if (this.bgImage) {
      this.bgImage.setPosition(0, 0);
      this.bgImage.setDisplaySize(width, height);
    }
  }

  /** Clean up all blur resources. */
  destroy(): void {
    this._captured = false;
    if (this.bgImage) {
      const plugin = this.scene.plugins.get('rexKawaseBlurPipeline') as
        | KawaseBlurFilterPipelinePlugin
        | undefined;
      plugin?.remove(this.bgImage, this.blurName);
      this.bgImage.resetPipeline(true);
      this.bgImage.postFX?.clear();
      this.bgImage.setVisible(false);
      this.bgImage.destroy();
      this.bgImage = undefined;
    }

    if (this.bgTexture) {
      this.bgTexture.destroy();
      this.bgTexture = undefined;
    }
  }
}
